<template>
  <div class="tgt-root">
    <!-- ── Top bar ─────────────────────────────────────────────────────── -->
    <header class="tgt-topbar">
      <div class="brand">
        <button class="back-btn" @click="goBack" title="Back to dashboard"><v-icon size="20" color="#14663a">mdi-arrow-left</v-icon></button>
        <div class="brand-logo-box"><img :src="wtiLogo" class="brand-logo" alt="WTI" /></div>
        <div class="brand-text">
          <div class="brand-title">Performance Targets</div>
          <div class="brand-sub">Employee KPI target-sheet status</div>
        </div>
      </div>
      <div class="topbar-actions">
        <span class="fy-chip" v-if="financialYear">FY {{ financialYear }}</span>
        <download-excel v-if="overdueRows.length" class="btn ghost dl-btn overdue" :data="overdueRows" :fields="pendingFields" :name="'WTI-overdue-targets-' + financialYear + '.xls'" worksheet="Overdue Targets" :title="overdueSheetTitle" type="xls">
          <v-icon size="17" color="#c62828">mdi-file-excel-outline</v-icon><span>Overdue targets ({{ overdueRows.length }})</span>
        </download-excel>
        <download-excel v-if="pendingRows.length" class="btn ghost dl-btn" :data="pendingRows" :fields="pendingFields" :name="'WTI-pending-targets-' + financialYear + '.xls'" worksheet="Pending Targets" :title="pendingSheetTitle" type="xls">
          <v-icon size="17" color="#14663a">mdi-file-excel-outline</v-icon><span>All pending ({{ pendingRows.length }})</span>
        </download-excel>
        <button class="btn ghost" :disabled="loading" @click="fetchData"><v-icon size="17" :class="{ spin: loading }">mdi-refresh</v-icon><span>Refresh</span></button>
        <button class="btn solid" @click="logout"><v-icon size="17">mdi-logout</v-icon><span>Logout</span></button>
      </div>
    </header>

    <div v-if="loading" class="loader-wrap"><v-progress-circular indeterminate color="#14663a" size="52" width="4" /></div>

    <main v-else class="tgt-body">
      <div v-if="error" class="banner error"><v-icon size="18" color="#c62828">mdi-alert-circle-outline</v-icon> {{ error }}</div>

      <!-- ── Scope: active vs resigned/left staff ──────────────────────── -->
      <section class="scope-row">
        <div class="view-toggle scope">
          <button :class="['vt', scope === 'active' && 'on']" @click="setScope('active')"><v-icon size="16">mdi-account-check</v-icon> Active staff</button>
          <button :class="['vt', scope === 'resigned' && 'on']" @click="setScope('resigned')"><v-icon size="16">mdi-account-arrow-right</v-icon> Resigned / left</button>
        </div>
        <div v-if="scope === 'resigned'" class="scope-note resigned"><v-icon size="16" color="#b45309">mdi-information-outline</v-icon> Showing targets of staff who <b>resigned / left this financial year</b> — excluded from the current-year organisation totals.</div>
        <div v-else class="scope-note"><v-icon size="16" color="#14663a">mdi-shield-check-outline</v-icon> Active staff only. <b>{{ resignedCount }}</b> who left this FY are excluded from these totals.</div>
      </section>

      <!-- ── Summary tiles ─────────────────────────────────────────────── -->
      <section class="sum-grid">
        <div class="sum-card" :style="{ '--c': '#14663a' }"><div class="sum-v">{{ num(summary.total) }}</div><div class="sum-l">Total Targets</div></div>
        <div class="sum-card" :style="{ '--c': '#64748b' }"><div class="sum-v">{{ num(summary.notSubmitted) }}</div><div class="sum-l">Not Submitted</div></div>
        <div class="sum-card" :style="{ '--c': '#1565c0' }"><div class="sum-v">{{ num(summary.pending) }}</div><div class="sum-l">Pending Approval</div></div>
        <div class="sum-card" :style="{ '--c': '#2e7d32' }"><div class="sum-v">{{ num(summary.approved) }}</div><div class="sum-l">Approved</div></div>
        <div class="sum-card" :style="{ '--c': '#8e24aa' }"><div class="sum-v">{{ num(summary.employees) }}</div><div class="sum-l">Employees</div></div>
      </section>

      <!-- ── Approval SLA ──────────────────────────────────────────────── -->
      <section class="sla-strip">
        <div class="sla-title"><v-icon size="16" color="#14663a">mdi-timer-outline</v-icon> Approval SLA</div>
        <div class="sla">
          <v-icon size="18" color="#1565c0">mdi-timer-sand</v-icon>
          <span><b>{{ summary.approval.avgPendingDays != null ? summary.approval.avgPendingDays + ' days' : '—' }}</b> avg pending age</span>
          <span class="sla-sub">{{ summary.approval.pendingCount }} awaiting approval</span>
        </div>
        <div class="sla">
          <v-icon size="18" color="#c62828">mdi-clock-alert-outline</v-icon>
          <span><b>{{ summary.approval.oldestPendingDays != null ? summary.approval.oldestPendingDays + ' days' : '—' }}</b> oldest pending</span>
        </div>
        <div class="sla">
          <v-icon size="18" color="#2e7d32">mdi-check-decagram-outline</v-icon>
          <span><b>{{ summary.approval.avgApprovalDays != null ? summary.approval.avgApprovalDays + ' days' : 'No approvals yet' }}</b> avg approval time</span>
          <span class="sla-sub" v-if="summary.approval.approvedCount">{{ summary.approval.approvedCount }} approved</span>
        </div>
      </section>

      <!-- ── Org target outcomes ──────────────────────────────────────── -->
      <section class="card outcome-card">
        <div class="oc-head">
          <div class="oc-title"><v-icon size="18" color="#14663a">mdi-target-account</v-icon> Organisation Target Outcomes</div>
          <div class="oc-headline">
            <span class="oc-big">{{ summary.outcome.overallMetPct }}%</span>
            <span class="oc-big-l">met (on-time + delayed)</span>
          </div>
        </div>
        <div class="oc-bar">
          <div class="ocseg" :style="{ width: summary.outcome.metOnTimePct + '%', background: '#2e7d32' }" title="Met on time"></div>
          <div class="ocseg" :style="{ width: summary.outcome.delayedMetPct + '%', background: '#e08600' }" title="Delayed Met"></div>
          <div class="ocseg" :style="{ width: summary.outcome.notMetPct + '%', background: '#c62828' }" title="Not Met"></div>
          <div class="ocseg" :style="{ width: summary.outcome.pendingNotDuePct + '%', background: '#94a3b8' }" title="Pending (not due)"></div>
        </div>
        <div class="oc-legend">
          <button :class="['ocl', outcomeMode === 'met' && 'on']" @click="loadOutcome('met')"><span class="ocdot" style="background:#2e7d32"></span> Met on time <b>{{ summary.outcome.metOnTime }}</b> · {{ summary.outcome.metOnTimePct }}%</button>
          <button :class="['ocl', outcomeMode === 'delayed' && 'on']" @click="loadOutcome('delayed')"><span class="ocdot" style="background:#e08600"></span> Delayed Met <b>{{ summary.outcome.delayedMet }}</b> · {{ summary.outcome.delayedMetPct }}%</button>
          <button :class="['ocl', outcomeMode === 'notmet' && 'on']" @click="loadOutcome('notmet')"><span class="ocdot" style="background:#c62828"></span> Not Met <b>{{ summary.outcome.notMet }}</b> · {{ summary.outcome.notMetPct }}%</button>
          <button :class="['ocl', outcomeMode === 'pending' && 'on']" @click="loadOutcome('pending')"><span class="ocdot" style="background:#94a3b8"></span> Pending — not due <b>{{ summary.outcome.pendingNotDue }}</b> · {{ summary.outcome.pendingNotDuePct }}%</button>
        </div>
      </section>

      <!-- ── Outcome target list (opens when an outcome is clicked) ─────── -->
      <section v-if="outcomeMode" class="card outlist-card">
        <div class="outlist-head">
          <span><v-icon size="17" :color="outcomeMeta.color">{{ outcomeMeta.icon }}</v-icon> {{ outcomeMeta.label }} · <b>{{ outcomeRows.length }}</b> targets</span>
          <button class="clear-btn" @click="clearOutcome"><v-icon size="16">mdi-close</v-icon> Clear</button>
        </div>
        <div v-if="outcomeLoading" class="det-loading"><v-progress-circular indeterminate color="#14663a" size="24" width="2" /> Loading…</div>
        <div v-else class="det-wrap">
          <table class="det-table">
            <thead><tr><th>Target</th><th>Employee</th><th>Project</th><th class="r">Wt</th><th>Due date</th><th>Status</th></tr></thead>
            <tbody>
              <tr v-for="(t, i) in outcomeRows" :key="i">
                <td class="tgt-text">{{ t.target || '—' }}</td>
                <td>{{ t.name }} <span class="dim">{{ t.employeeId ? '· ' + t.employeeId : '' }}</span></td>
                <td><span class="proj-chip">{{ t.division }}</span></td>
                <td class="r">{{ t.weightage }}</td>
                <td class="dim">{{ t.targetDate ? fmtDate(t.targetDate) : '—' }}</td>
                <td class="dim">{{ t.status }}</td>
              </tr>
              <tr v-if="!outcomeRows.length"><td colspan="6" class="det-empty">No targets in this bucket.</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- ── Controls ──────────────────────────────────────────────────── -->
      <section class="controls">
        <div class="view-toggle">
          <button :class="['vt', view === 'employee' && 'on']" @click="view = 'employee'"><v-icon size="16">mdi-account-multiple</v-icon> By Employee</button>
          <button :class="['vt', view === 'project' && 'on']" @click="view = 'project'"><v-icon size="16">mdi-folder-multiple</v-icon> By Project</button>
        </div>
        <div class="filters">
          <div class="search-box">
            <v-icon size="17" color="#8a978f">mdi-magnify</v-icon>
            <input v-model="search" type="text" placeholder="Search name or ID" />
          </div>
          <select v-model="divisionFilter" class="sel">
            <option value="">All projects</option>
            <option v-for="d in divisions" :key="d" :value="d">{{ d }}</option>
          </select>
          <select v-model="statusFilter" class="sel">
            <option value="all">All statuses</option>
            <option value="pending">Has pending</option>
            <option value="approved">Has approved</option>
            <option value="notsubmitted">Not submitted</option>
          </select>
        </div>
      </section>

      <div class="result-note">{{ filtered.length }} employee{{ filtered.length === 1 ? '' : 's' }}</div>

      <!-- ── By Employee ───────────────────────────────────────────────── -->
      <section v-if="view === 'employee'" class="card table-card">
        <table class="tgt-table">
          <thead>
            <tr><th></th><th>Name</th><th>Emp ID</th><th>Project</th><th class="r">Targets</th><th class="r">Met %</th><th class="r">Approved</th><th class="r">Pending</th></tr>
          </thead>
          <tbody>
            <template v-for="e in filtered">
              <tr :key="e.id" class="emp-row" @click="toggle(e.id)">
                <td class="chev"><v-icon size="18" color="#8a978f">{{ expandedId === e.id ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon></td>
                <td class="strong">{{ e.name }}</td>
                <td class="dim">{{ e.employeeId || '—' }}</td>
                <td><span class="proj-chip">{{ e.division }}</span></td>
                <td class="r">{{ e.totalTargets }}</td>
                <td class="r"><b :style="{ color: e.metPercent > 0 ? '#2e7d32' : '#9aa8a1' }">{{ e.metPercent }}%</b></td>
                <td class="r">{{ e.approved }}</td>
                <td class="r">{{ e.pending }}</td>
              </tr>
              <tr v-if="expandedId === e.id" :key="e.id + '-d'" class="detail-row">
                <td colspan="8">
                  <div v-if="detailLoading" class="det-loading"><v-progress-circular indeterminate color="#14663a" size="24" width="2" /> Loading targets…</div>
                  <div v-else-if="detail" class="det-wrap">
                    <table class="det-table">
                      <thead><tr><th>#</th><th>Target</th><th class="r">Wt</th><th>Due date</th><th>Date met</th><th>Delayed?</th><th>Stage</th></tr></thead>
                      <tbody>
                        <tr v-for="(t, i) in detail.targets" :key="i">
                          <td class="dim">{{ t.slNo || i + 1 }}</td>
                          <td class="tgt-text">{{ t.target || '—' }}</td>
                          <td class="r">{{ t.weightage }}</td>
                          <td class="dim nowrap">{{ t.targetDate ? fmtDate(t.targetDate) : '—' }}</td>
                          <td class="nowrap">{{ t.metDate ? fmtDate(t.metDate) : '—' }}</td>
                          <td class="nowrap">
                            <span v-if="t.delayed === true" class="del-pill del">Delayed · {{ t.delayedDays }}d</span>
                            <span v-else-if="t.delayed === false" class="del-pill ontime">On time</span>
                            <span v-else class="dim">—</span>
                          </td>
                          <td>
                            <span class="stage-pill" :style="stagePill(t.stage)">{{ t.stage }}</span>
                            <span v-if="t.pendingDays != null" class="wait-note">{{ t.pendingDays }}d</span>
                          </td>
                        </tr>
                        <tr v-if="!detail.targets.length"><td colspan="7" class="det-empty">No targets.</td></tr>
                      </tbody>
                    </table>
                  </div>
                </td>
              </tr>
            </template>
            <tr v-if="!filtered.length"><td colspan="8" class="det-empty">No employees match the filters.</td></tr>
          </tbody>
        </table>
      </section>

      <!-- ── By Project ────────────────────────────────────────────────── -->
      <section v-else class="proj-view">
        <div v-for="g in grouped" :key="g.division" class="card proj-card">
          <div class="proj-head">
            <div class="proj-name"><v-icon size="18" color="#14663a">mdi-folder</v-icon> {{ g.division }}</div>
            <div class="proj-stats">
              <span>{{ g.employees.length }} staff</span>
              <span>{{ g.total }} targets</span>
              <span class="pchip">{{ g.metPct }}% met</span>
              <span class="pchip green">Met on time {{ g.metOnTime }}</span>
              <span class="pchip amber">Delayed {{ g.delayedMet }}</span>
              <span class="pchip red">Not Met {{ g.notMet }}</span>
              <span class="pchip grey">Pending — not due {{ g.pendingNotDue }}</span>
            </div>
          </div>
          <table class="tgt-table compact">
            <tbody>
              <template v-for="e in g.employees">
                <tr :key="e.id" class="emp-row" @click="toggle(e.id)">
                  <td class="chev"><v-icon size="18" color="#8a978f">{{ expandedId === e.id ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon></td>
                  <td class="strong">{{ e.name }}</td>
                  <td class="dim">{{ e.employeeId || '—' }}</td>
                  <td class="r">{{ e.totalTargets }} targets</td>
                  <td class="r"><b :style="{ color: e.metPercent > 0 ? '#2e7d32' : '#9aa8a1' }">{{ e.metPercent }}%</b></td>
                </tr>
                <tr v-if="expandedId === e.id" :key="e.id + '-d'" class="detail-row">
                  <td colspan="5">
                    <div v-if="detailLoading" class="det-loading"><v-progress-circular indeterminate color="#14663a" size="24" width="2" /> Loading…</div>
                    <div v-else-if="detail" class="det-wrap">
                      <table class="det-table">
                        <thead><tr><th>#</th><th>Target</th><th class="r">Wt</th><th>Stage</th></tr></thead>
                        <tbody>
                          <tr v-for="(t, i) in detail.targets" :key="i">
                            <td class="dim">{{ t.slNo || i + 1 }}</td><td class="tgt-text">{{ t.target || '—' }}</td>
                            <td class="r">{{ t.weightage }}</td><td>
                            <span class="stage-pill" :style="stagePill(t.stage)">{{ t.stage }}</span>
                            <span v-if="t.pendingDays != null" class="wait-note">{{ t.pendingDays }}d waiting</span>
                            <span v-else-if="t.approvalDays != null" class="wait-note ok">approved in {{ t.approvalDays }}d</span>
                          </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
        <div v-if="!grouped.length" class="card"><div class="det-empty">No employees match the filters.</div></div>
      </section>
    </main>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "CeoTargets",
  data() {
    return {
      loading: true, error: "", financialYear: "",
      summary: {
        total: 0, notSubmitted: 0, pending: 0, approved: 0, employees: 0,
        approval: { avgApprovalDays: null, avgPendingDays: null, oldestPendingDays: null, approvedCount: 0, pendingCount: 0 },
        outcome: { total: 0, metOnTime: 0, delayedMet: 0, notMet: 0, pendingNotDue: 0, metOnTimePct: 0, delayedMetPct: 0, notMetPct: 0, pendingNotDuePct: 0, overallMetPct: 0 },
      },
      divisions: [], employees: [],
      scope: "active", resignedCount: 0,
      pendingRows: [], pendingSummary: { total: 0, overdue: 0, dueThisMonth: 0, upcoming: 0, noDueDate: 0 },
      pendingFields: {
        "Employee": "employee",
        "Emp ID": "employeeId",
        "Division / Project": "division",
        "Target": "target",
        "Weightage": "weightage",
        "Due Date": "dueDate",
        "Status": "status",
        "Days (overdue / left)": "days",
        "Submission": "stage",
      },
      view: "employee", search: "", divisionFilter: "", statusFilter: "all",
      expandedId: null, detail: null, detailLoading: false, detailCache: {},
      outcomeMode: "", outcomeRows: [], outcomeLoading: false,
      wtiLogo: require("@/assets/images/wtilogo.png"),
    };
  },
  computed: {
    filtered() {
      const q = this.search.trim().toLowerCase();
      return this.employees.filter((e) => {
        if (this.divisionFilter && e.division !== this.divisionFilter) return false;
        if (q && !((e.name || "").toLowerCase().includes(q) || String(e.employeeId || "").toLowerCase().includes(q))) return false;
        if (this.statusFilter === "pending" && e.pending <= 0) return false;
        if (this.statusFilter === "approved" && e.approved <= 0) return false;
        if (this.statusFilter === "notsubmitted" && e.notSubmitted <= 0) return false;
        return true;
      });
    },
    grouped() {
      const map = {};
      this.filtered.forEach((e) => {
        if (!map[e.division]) map[e.division] = { division: e.division, employees: [], total: 0, metOnTime: 0, delayedMet: 0, notMet: 0, pendingNotDue: 0 };
        const g = map[e.division];
        g.employees.push(e);
        g.total += e.totalTargets;
        g.metOnTime += (e.metOnTime || 0);
        g.delayedMet += (e.delayedMet || 0);
        g.notMet += (e.notMet || 0);
        g.pendingNotDue += (e.pendingNotDue || 0);
      });
      return Object.values(map)
        .map((g) => ({ ...g, metPct: g.total ? Math.round(((g.metOnTime + g.delayedMet) / g.total) * 1000) / 10 : 0 }))
        .sort((a, b) => b.employees.length - a.employees.length);
    },
    outcomeMeta() {
      const m = {
        met: { label: "Met on time", color: "#2e7d32", icon: "mdi-check-circle" },
        delayed: { label: "Delayed Met", color: "#e08600", icon: "mdi-clock-check" },
        notmet: { label: "Not Met (overdue)", color: "#c62828", icon: "mdi-close-circle" },
        pending: { label: "Pending — not due", color: "#64748b", icon: "mdi-timer-sand" },
      };
      return m[this.outcomeMode] || { label: "", color: "#14663a", icon: "mdi-target" };
    },
    pendingSheetTitle() {
      const s = this.pendingSummary;
      return "WTI Pending Targets · FY " + this.financialYear + " · " + s.total + " open · " + s.overdue + " overdue · " + s.dueThisMonth + " due this month";
    },
    overdueRows() {
      return (this.pendingRows || []).filter((r) => r.status === "Overdue");
    },
    overdueSheetTitle() {
      return "WTI Overdue Targets · FY " + this.financialYear + " · " + this.overdueRows.length + " overdue (past due date)";
    },
  },
  async mounted() {
    if (this.$route.query.scope === "resigned") this.scope = "resigned";
    await this.fetchData();
    this.fetchPendingTargets();
    if (this.$route.query.emp) this.search = String(this.$route.query.emp);
    if (this.$route.query.outcome) this.loadOutcome(this.$route.query.outcome);
  },
  methods: {
    authHeaders() { return { "x-auth-token": localStorage.getItem("token") }; },
    async fetchPendingTargets() {
      try {
        const res = await axios({ method: "GET", url: "dashboard/ceo/pending-targets", headers: this.authHeaders() });
        if (res.data && res.data.status) { this.pendingRows = res.data.data || []; this.pendingSummary = res.data.summary || this.pendingSummary; }
      } catch (e) { /* button hidden if empty */ }
    },
    setScope(s) {
      if (this.scope === s) return;
      this.scope = s;
      this.clearOutcome();
      this.expandedId = null; this.detail = null;
      this.fetchData();
    },
    async loadOutcome(key) {
      if (this.outcomeMode === key) { this.clearOutcome(); return; }
      this.outcomeMode = key; this.outcomeLoading = true; this.outcomeRows = [];
      try {
        const res = await axios({ method: "GET", url: "dashboard/ceo/targets-by-outcome", params: { outcome: key }, headers: this.authHeaders() });
        this.outcomeRows = (res.data && res.data.status) ? res.data.data : [];
      } catch (e) { this.outcomeRows = []; }
      finally { this.outcomeLoading = false; }
    },
    clearOutcome() { this.outcomeMode = ""; this.outcomeRows = []; },
    fmtDate(d) { return d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : ""; },
    async fetchData() {
      this.loading = true; this.error = "";
      try {
        const res = await axios({ method: "GET", url: "dashboard/ceo/employee-targets", params: { scope: this.scope }, headers: this.authHeaders() });
        if (res.data && res.data.status) {
          this.employees = res.data.data || [];
          this.summary = res.data.summary || this.summary;
          this.resignedCount = (res.data.summary && res.data.summary.resignedCount) || 0;
          this.divisions = res.data.divisions || [];
          this.financialYear = res.data.financialYear || "";
        } else { this.error = (res.data && res.data.msg) || "Failed to load targets."; }
      } catch (e) { this.error = "Unable to reach the server."; }
      finally { this.loading = false; }
    },
    async toggle(id) {
      if (this.expandedId === id) { this.expandedId = null; return; }
      this.expandedId = id;
      if (this.detailCache[id]) { this.detail = this.detailCache[id]; return; }
      this.detail = null; this.detailLoading = true;
      try {
        const res = await axios({ method: "GET", url: "dashboard/ceo/employee-target-detail", params: { employeeId: id }, headers: this.authHeaders() });
        this.detail = (res.data && res.data.status) ? res.data : { targets: [] };
        this.detailCache[id] = this.detail;
      } catch (e) { this.detail = { targets: [] }; }
      finally { this.detailLoading = false; }
    },
    stagePill(stage) {
      const m = { "Approved": ["#eaf4ee", "#2e7d32"], "Pending Approval": ["#e7f0fb", "#1565c0"], "Rejected": ["#fdecec", "#c62828"], "Not Submitted": ["#f1f4f2", "#64748b"] };
      const c = m[stage] || m["Not Submitted"];
      return { background: c[0], color: c[1] };
    },
    goBack() { this.$router.push("/ceo-dashboard"); },
    logout() { this.$store.commit("logoutUser"); },
    num(n) { return Number(n || 0).toLocaleString("en-IN"); },
  },
};
</script>

<style scoped>
.tgt-root { min-height: 100vh; width: 100%; background: linear-gradient(180deg, #f3f7f4 0%, #e9f0eb 100%); font-family: "Poppins", "Segoe UI", system-ui, sans-serif; color: #1b2320; }
.tgt-topbar { position: sticky; top: 0; z-index: 20; display: flex; align-items: center; justify-content: space-between; padding: 12px 26px; background: rgba(255,255,255,0.94); backdrop-filter: saturate(180%) blur(12px); border-bottom: 1px solid rgba(20,102,58,0.12); }
.brand { display: flex; align-items: center; gap: 12px; }
.back-btn { background: #fff; border: 1px solid rgba(20,102,58,0.2); border-radius: 10px; width: 38px; height: 38px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background 0.15s; }
.back-btn:hover { background: #eef6f0; }
.brand-logo-box { width: 46px; height: 46px; border-radius: 12px; display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg, #14663a, #2e7d32); box-shadow: 0 6px 16px rgba(20,102,58,0.3); padding: 6px; }
.brand-logo { max-width: 100%; max-height: 100%; object-fit: contain; }
.brand-title { font-size: 17px; font-weight: 700; }
.brand-sub { font-size: 11.5px; color: #6b7a72; margin-top: -2px; }
.topbar-actions { display: flex; align-items: center; gap: 12px; }
.fy-chip { font-size: 12px; font-weight: 700; color: #14663a; background: #e5f1ea; border: 1px solid #cfe5d8; padding: 5px 12px; border-radius: 20px; }
.btn { display: inline-flex; align-items: center; gap: 7px; border: none; cursor: pointer; font-size: 13px; font-weight: 600; padding: 8px 14px; border-radius: 10px; font-family: inherit; }
.btn.ghost { background: #fff; color: #14663a; border: 1px solid rgba(20,102,58,0.2); }
.btn.solid { background: linear-gradient(135deg, #14663a, #2e7d32); color: #fff; }
.btn.dl-btn { background: #eef6f0; color: #14663a; border: 1px solid #cfe5d8; cursor: pointer; }
.btn.dl-btn:hover { background: #e2f0e8; }
.btn.dl-btn.overdue { background: #fdecec; color: #c62828; border-color: #f3c9c9; }
.btn.dl-btn.overdue:hover { background: #fbe0e0; }
.spin { animation: spin 0.9s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }
.loader-wrap { display: flex; justify-content: center; align-items: center; min-height: 70vh; }
.tgt-body { max-width: 1240px; margin: 0 auto; padding: 22px 26px 44px; }
.banner.error { display: flex; gap: 8px; background: #fdecec; color: #c62828; border: 1px solid #f5c6c6; padding: 10px 14px; border-radius: 12px; margin-bottom: 16px; font-size: 13px; }

.sum-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 14px; margin-bottom: 18px; }
.sum-card { background: #fff; border-radius: 14px; padding: 16px 18px; box-shadow: 0 3px 14px rgba(20,40,30,0.05); border: 1px solid #edf2ee; border-left: 4px solid var(--c); }
.sum-v { font-size: 26px; font-weight: 700; color: #14432a; }
.sum-l { font-size: 12px; color: #6b7a72; margin-top: 2px; }

.outcome-card { padding: 18px 20px; margin-bottom: 18px; }
.oc-head { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; margin-bottom: 14px; }
.oc-title { font-size: 14px; font-weight: 700; color: #1b3a2a; display: flex; align-items: center; gap: 8px; }
.oc-headline { display: flex; align-items: baseline; gap: 8px; }
.oc-big { font-size: 30px; font-weight: 800; color: #14663a; }
.oc-big-l { font-size: 12px; color: #8a978f; }
.oc-bar { display: flex; height: 16px; border-radius: 8px; overflow: hidden; background: #eef2f0; }
.ocseg { transition: width 0.4s ease; min-width: 0; }
.oc-legend { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 12px; }
.ocl { display: flex; align-items: center; gap: 6px; font-family: inherit; font-size: 12.5px; color: #5a6b62; cursor: pointer; background: #fff; border: 1px solid #e2e8e4; padding: 6px 12px; border-radius: 16px; transition: all 0.15s ease; }
.ocl:hover { border-color: #14663a; background: #f4faf6; transform: translateY(-1px); }
.ocl.on { border-color: #14663a; background: #eaf4ee; }
.ocl b { color: #23302a; margin: 0 2px; }

.outlist-card { padding: 16px 18px; margin-bottom: 18px; border: 1px solid #cfe5d8; }
.outlist-head { display: flex; align-items: center; justify-content: space-between; font-size: 14px; font-weight: 600; color: #1b3a2a; margin-bottom: 10px; }
.outlist-head b { color: #14663a; }
.clear-btn { display: inline-flex; align-items: center; gap: 4px; font-family: inherit; font-size: 12px; font-weight: 600; color: #8a978f; background: #f4f7f5; border: 1px solid #e2e8e4; border-radius: 16px; padding: 5px 11px; cursor: pointer; }
.clear-btn:hover { color: #c62828; }
.ocdot { width: 10px; height: 10px; border-radius: 3px; }

.sla-strip { display: flex; align-items: center; flex-wrap: wrap; gap: 22px; background: #fff; border: 1px solid #edf2ee; border-radius: 14px; padding: 12px 18px; margin-bottom: 18px; box-shadow: 0 3px 14px rgba(20,40,30,0.05); }
.sla-title { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; color: #14663a; display: flex; align-items: center; gap: 6px; }
.sla { display: flex; align-items: center; gap: 7px; font-size: 13px; color: #5a6b62; }
.sla b { color: #23302a; font-size: 14px; }
.sla-sub { font-size: 11.5px; color: #9aa8a1; background: #f4f7f5; padding: 2px 9px; border-radius: 12px; }
.wait-note { display: inline-block; margin-left: 8px; font-size: 10.5px; font-weight: 600; color: #b56b00; background: #fdf3e3; padding: 2px 8px; border-radius: 10px; }
.wait-note.ok { color: #2e7d32; background: #eaf4ee; }

.scope-row { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; margin-bottom: 16px; }
.scope-note { display: flex; align-items: center; gap: 7px; font-size: 12.5px; color: #5a6b62; }
.scope-note b { color: #23302a; }
.scope-note.resigned { color: #7a4a08; background: #fdf6ec; border: 1px solid #f2d19b; padding: 7px 14px; border-radius: 10px; }
.scope-note.resigned b { color: #b45309; }
.view-toggle.scope .vt.on { background: #b45309; }
.view-toggle.scope .vt:first-child.on { background: #14663a; }

.controls { display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap; margin-bottom: 10px; }
.view-toggle { display: flex; background: #fff; border: 1px solid #dcebe1; border-radius: 10px; overflow: hidden; }
.vt { display: inline-flex; align-items: center; gap: 6px; border: none; background: transparent; cursor: pointer; font-family: inherit; font-size: 13px; font-weight: 600; color: #5a6b62; padding: 9px 16px; }
.vt.on { background: #14663a; color: #fff; }
.filters { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.search-box { display: flex; align-items: center; gap: 6px; background: #fff; border: 1px solid #dcebe1; border-radius: 10px; padding: 7px 12px; }
.search-box input { border: none; outline: none; font-family: inherit; font-size: 13px; background: transparent; width: 180px; color: #23302a; }
.sel { font-family: inherit; font-size: 13px; color: #23302a; background: #fff; border: 1px solid #dcebe1; border-radius: 10px; padding: 8px 12px; cursor: pointer; }
.result-note { font-size: 12px; color: #8a978f; margin: 4px 2px 12px; }

.card { background: #fff; border-radius: 16px; box-shadow: 0 3px 14px rgba(20,40,30,0.05); border: 1px solid #edf2ee; }
.table-card { overflow: hidden; }
.tgt-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.tgt-table thead th { text-align: left; background: #f7faf8; color: #8a978f; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.3px; padding: 10px 14px; }
.tgt-table td { padding: 11px 14px; border-top: 1px solid #f1f4f2; color: #33403a; }
.tgt-table .r, .det-table .r { text-align: right; }
.emp-row { cursor: pointer; transition: background 0.12s; }
.emp-row:hover { background: #f7faf8; }
.chev { width: 30px; }
.strong { font-weight: 600; color: #23302a; }
.dim { color: #9aa8a1; }
.proj-chip { font-size: 11px; background: #eef6f0; color: #14663a; padding: 3px 9px; border-radius: 12px; }
.detail-row td { background: #fafcfb; padding: 0 14px 14px 44px; }
.det-loading { display: flex; align-items: center; gap: 8px; color: #6b7a72; font-size: 13px; padding: 14px 0; }
.det-wrap { overflow-x: auto; }
.det-table { width: 100%; border-collapse: collapse; font-size: 12.5px; margin-top: 6px; }
.det-table th { text-align: left; color: #8a978f; font-weight: 600; font-size: 10.5px; text-transform: uppercase; padding: 6px 10px; border-bottom: 1px solid #eef2f0; }
.det-table td { padding: 8px 10px; border-bottom: 1px solid #f4f7f5; color: #33403a; vertical-align: top; }
.tgt-text { max-width: 620px; line-height: 1.4; }
.stage-pill { font-size: 10.5px; font-weight: 600; padding: 2px 9px; border-radius: 12px; white-space: nowrap; }
.nowrap { white-space: nowrap; }
.del-pill { font-size: 10.5px; font-weight: 700; padding: 2px 9px; border-radius: 12px; white-space: nowrap; }
.del-pill.del { background: #fdecec; color: #c62828; }
.del-pill.ontime { background: #eaf4ee; color: #2e7d32; }
.del-pill.over { background: #fbf1e2; color: #b56b00; }
.det-empty { text-align: center; color: #9aa8a1; padding: 20px; }

.proj-view { display: flex; flex-direction: column; gap: 16px; }
.proj-card { padding: 6px 6px 10px; }
.proj-head { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px; padding: 14px 16px 10px; }
.proj-name { font-size: 15px; font-weight: 700; color: #1b3a2a; display: flex; align-items: center; gap: 8px; }
.proj-stats { display: flex; align-items: center; gap: 12px; font-size: 12px; color: #6b7a72; flex-wrap: wrap; }
.pchip { background: #eef6f0; color: #14663a; padding: 3px 10px; border-radius: 12px; font-weight: 600; }
.pchip.green { background: #eaf4ee; color: #2e7d32; } .pchip.blue { background: #e7f0fb; color: #1565c0; }
.pchip.amber { background: #fbf1e2; color: #b56b00; } .pchip.red { background: #fdecec; color: #c62828; } .pchip.grey { background: #f1f4f2; color: #64748b; }
.tgt-table.compact td { padding: 9px 14px; }

@media (max-width: 800px) {
  .controls { flex-direction: column; align-items: stretch; }
  .search-box input { width: 100%; }
  .tgt-body { padding: 16px; }
}
</style>
